import React, { useState, useEffect } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography, IconButton, Menu, MenuItem, Snackbar, Alert } from '@mui/material';
import { Share as ShareIcon, LocationOn as MemoryIcon, Map as StateIcon } from '@mui/icons-material';
import PublicProfileStaticMap from '../PublicProfileStaticMap';
import CompositeStatesMapImage from './CompositeStatesMapImage';
import OpenGraphMapMeta from './OpenGraphMapMeta';
import { stateDetectionService } from '../../services/stateDetectionService';

interface PublicProfileMapsProps {
  memories: any[];
  user: {
    firstName?: string;
    lastName?: string;
    username: string;
    publicUsername?: string;
  } | null;
  onMemoryClick?: (memory: any) => void;
}

type MapViewType = 'memories' | 'states';

const PublicProfileMaps: React.FC<PublicProfileMapsProps> = ({
  memories,
  user,
  onMemoryClick
}) => {
  const [mapView, setMapView] = useState<MapViewType>('memories');
  const [visitedStates, setVisitedStates] = useState<string[]>([]);
  const [stateStats, setStateStats] = useState<{ [state: string]: number }>({});
  const [shareMenuAnchor, setShareMenuAnchor] = useState<null | HTMLElement>(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  // Calculate visited states from memories
  useEffect(() => {
    console.log('Analyzing memories for states:', memories.length, 'memories');
    console.log('Sample memory:', memories[0]);
    
    const states = stateDetectionService.getVisitedStates(memories);
    const stats = stateDetectionService.getStateStatistics(memories);
    
    console.log('Detected states:', states);
    console.log('State stats:', stats);
    
    setVisitedStates(states);
    setStateStats(stats);
  }, [memories]);

  const handleMapViewChange = (event: React.MouseEvent<HTMLElement>, newView: MapViewType | null) => {
    if (newView !== null) {
      setMapView(newView);
    }
  };

  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareMenuAnchor(null);
  };

  const generateShareUrl = (platform: string) => {
    if (!user) return '';
    
    const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
    const profileUrl = `https://fojourn.site/u/${user.publicUsername || user.username}`;
    
    const mapTypeText = mapView === 'memories' 
      ? `${memories.length} travel memories`
      : `${visitedStates.length} states visited`;
    
    const shareText = `Check out ${displayName}'s ${mapTypeText} on Fojourn!`;
    const shareUrl = `${profileUrl}?view=${mapView}`;

    switch (platform) {
      case 'twitter':
        return `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
      case 'facebook':
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
      case 'linkedin':
        return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setShareSuccess(true);
        return null;
      default:
        return shareUrl;
    }
  };

  const handleShare = (platform: string) => {
    const url = generateShareUrl(platform);
    if (url) {
      window.open(url, '_blank', 'width=600,height=400');
    }
    handleShareClose();
  };

  const mapViewStats = {
    memories: `${memories.length} memories`,
    states: `${visitedStates.length} states visited`
  };

  if (!user) {
    return null;
  }

  return (
    <Box sx={{ width: '100%', mb: 3 }}>
      {/* Open Graph Meta Tags */}
      <OpenGraphMapMeta
        user={user}
        mapType={mapView}
        memories={memories}
        visitedStates={visitedStates}
      />

      {/* Map View Toggle and Share Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            value={mapView}
            exclusive
            onChange={handleMapViewChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: 2,
                py: 1,
                border: '1px solid #e0e0e0',
                '&.Mui-selected': {
                  backgroundColor: '#1976d2',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#1565c0',
                  }
                }
              }
            }}
          >
            <ToggleButton value="memories">
              <MemoryIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              Memory Locations
            </ToggleButton>
            <ToggleButton value="states">
              <StateIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
              Visited States
            </ToggleButton>
          </ToggleButtonGroup>
          
          <Typography variant="body2" color="text.secondary">
            {mapViewStats[mapView]}
          </Typography>
        </Box>

        <IconButton
          onClick={handleShareClick}
          size="small"
          sx={{
            backgroundColor: '#f5f5f5',
            '&:hover': { backgroundColor: '#e0e0e0' }
          }}
        >
          <ShareIcon />
        </IconButton>
      </Box>

      {/* Map Display */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        minHeight: '400px',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}>
        {mapView === 'memories' ? (
          <PublicProfileStaticMap 
            memories={memories} 
            onMemoryClick={onMemoryClick}
          />
        ) : (
          <CompositeStatesMapImage 
            visitedStates={visitedStates}
            stateStats={stateStats}
            user={user}
            width={800}
            height={600}
          />
        )}
      </Box>

      {/* Share Menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={handleShareClose}
        PaperProps={{
          sx: { minWidth: '200px' }
        }}
      >
        <MenuItem onClick={() => handleShare('twitter')}>
          Share on Twitter
        </MenuItem>
        <MenuItem onClick={() => handleShare('facebook')}>
          Share on Facebook
        </MenuItem>
        <MenuItem onClick={() => handleShare('linkedin')}>
          Share on LinkedIn
        </MenuItem>
        <MenuItem onClick={() => handleShare('copy')}>
          Copy Link
        </MenuItem>
      </Menu>

      {/* Share Success Notification */}
      <Snackbar
        open={shareSuccess}
        autoHideDuration={3000}
        onClose={() => setShareSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShareSuccess(false)} severity="success">
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicProfileMaps;